import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/ui-helpers";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, FileText, Printer, MessageCircle, User } from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, tone }) {
  const c = tone === "danger" ? "text-[hsl(var(--danger))]" : "";
  return <div className="rounded-lg bg-secondary/40 p-3"><p className="text-[11px] text-muted-foreground">{label}</p><p className={`font-heading text-lg font-semibold ${c}`}>{value}</p></div>;
}

export default function CustomerDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fTrip, setFTrip] = useState("");
  const [fPay, setFPay] = useState("");
  const [fDeliv, setFDeliv] = useState("");
  const [trips, setTrips] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([api.get(`/customers/${cid}`), api.get("/trips")]);
      setData(r.data); setTrips(t.data);
    } catch { toast.error("Gagal memuat pelanggan"); }
    finally { setLoading(false); }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const downloadStatement = async (fmt) => {
    try {
      const token = localStorage.getItem("jastip_token");
      const res = await fetch(`${API}/customers/${cid}/statement/export?format=${fmt}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `statement.${fmt === "csv" ? "csv" : "pdf"}`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Unduhan dimulai");
    } catch { toast.error("Gagal mengunduh statement"); }
  };

  const waSummary = () => {
    if (!data) return;
    const num = (data.whatsapp || "").replace(/[^0-9]/g, "");
    if (!num) return toast.error("Nomor WhatsApp pelanggan kosong");
    const lines = (data.orders || []).filter((o) => o.status !== "DIBATALKAN").map((o) =>
      `• ${o.order_number}: ${formatIDR(o.order_total)} (${o.payment_status}, sisa ${formatIDR(o.balance)})`).join("\n");
    const msg = `Halo ${data.name}, berikut ringkasan pesanan Anda:\n\n${lines}\n\nTotal tagihan: ${formatIDR(data.summary.total_value)}\nSudah dibayar: ${formatIDR(data.summary.total_paid)}\nSisa: ${formatIDR(data.summary.outstanding_balance)}`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (loading) return <Loading />;
  if (!data) return <EmptyState title="Pelanggan tidak ditemukan" />;

  const orders = (data.orders || []).filter((o) =>
    (!fTrip || o.trip_id === fTrip) && (!fPay || o.payment_status === fPay) && (!fDeliv || o.delivery_status === fDeliv));
  const tripName = (id) => trips.find((t) => t.id === id)?.name || "-";

  return (
    <div>
      <PageHeader title={data.name} subtitle={`${data.whatsapp || ""} · ${data.city || ""}`}>
        <Button variant="outline" onClick={() => navigate("/customers")}><ArrowLeft className="h-4 w-4 mr-1.5" />Kembali</Button>
        <Button variant="outline" onClick={() => downloadStatement("pdf")} data-testid="statement-pdf-button"><FileText className="h-4 w-4 mr-1.5" />Statement PDF</Button>
        <Button variant="outline" onClick={() => window.print()} className="no-print"><Printer className="h-4 w-4 mr-1.5" />Cetak</Button>
        <Button variant="outline" onClick={waSummary} data-testid="customer-wa-button"><MessageCircle className="h-4 w-4 mr-1.5" />WhatsApp</Button>
      </PageHeader>

      <Tabs defaultValue="summary">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="summary" data-testid="cd-tab-summary">Ringkasan</TabsTrigger>
          <TabsTrigger value="orders" data-testid="cd-tab-orders">Semua Order</TabsTrigger>
          <TabsTrigger value="payments" data-testid="cd-tab-payments">Pembayaran</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="cd-tab-deliveries">Pengiriman</TabsTrigger>
          <TabsTrigger value="history" data-testid="cd-tab-history">Riwayat</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Total Order" value={data.summary.total_orders} />
            <Stat label="Produk Dipesan" value={data.summary.total_products} />
            <Stat label="Total Tagihan" value={formatIDR(data.summary.total_value)} />
            <Stat label="Total Dibayar" value={formatIDR(data.summary.total_paid)} />
            <Stat label="Sisa Tagihan" value={formatIDR(data.summary.outstanding_balance)} tone="danger" />
            <Stat label="Masih Dicari" value={data.summary.products_searching} />
            <Stat label="Belum Dibeli" value={data.summary.products_not_purchased} />
            <Stat label="Belum Dikirim" value={data.summary.products_not_delivered} />
          </div>
        </TabsContent>

        <TabsContent value="orders">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
            <SearchableSelect
              value={fTrip || "all"}
              onChange={(v) => setFTrip(v === "all" ? "" : v)}
              options={[{ value: "all", label: "Semua Trip", keywords: ["semua"] },
                ...trips.map((t) => ({ value: t.id, label: t.name, keywords: [t.name, t.code] }))]}
              placeholder="Trip"
              searchPlaceholder="Cari trip..."
              emptyText="Trip tidak ditemukan"
              testId="cd-filter-trip"
            />
            <Select value={fPay || "all"} onValueChange={(v) => setFPay(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="cd-filter-pay"><SelectValue placeholder="Status Bayar" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Status Bayar</SelectItem>{["Belum Dibayar","Dibayar Sebagian","Lunas","Refund Sebagian","Refund Penuh"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={fDeliv || "all"} onValueChange={(v) => setFDeliv(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="cd-filter-deliv"><SelectValue placeholder="Status Kirim" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua Status Kirim</SelectItem>{["SIAP DIANTAR","DIANTAR","SELESAI","-"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {orders.length === 0 ? <EmptyState title="Tidak ada order" /> : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.id} className="rounded-xl border border-border bg-card p-3" data-testid="cd-order-row">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold">{o.order_number}</span>
                      <span className="text-xs text-muted-foreground">{tripName(o.trip_id)} · {formatDate(o.order_date)}</span>
                      <StatusBadge status={o.status} /><StatusBadge status={o.payment_status} /><StatusBadge status={o.delivery_status} />
                    </div>
                    <div className="text-right"><p className="font-mono font-semibold">{formatIDR(o.order_total)}</p><p className="text-xs text-muted-foreground">Sisa {formatIDR(o.balance)}</p></div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(o.items || []).map((it, i) => (
                      <span key={i} className="text-xs bg-secondary/50 rounded-full px-2 py-0.5">{it.product_name} ×{it.quantity} {it.variant && `(${it.variant})`} · {formatIDR(it.final_price)}</span>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2 no-print">
                    <Button size="sm" variant="outline" onClick={() => navigate("/orders")} data-testid="cd-view-order">Lihat/Edit Order</Button>
                    <Button size="sm" variant="outline" onClick={() => navigate("/payments")}>Catat Bayar</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="payments">
          {(data.payments || []).length === 0 ? <EmptyState title="Belum ada pembayaran" /> : (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {data.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 border-b border-border last:border-0 text-sm">
                  <div><p className="font-medium">{p.order_number} · {p.method}</p><p className="text-xs text-muted-foreground">{p.sender_name || "Pengirim belum dicatat"} · {formatDate(p.payment_date)}</p></div>
                  <span className={`font-mono font-semibold ${p.is_refund ? "text-destructive" : ""}`}>{p.is_refund ? "-" : ""}{formatIDR(p.amount_idr)}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="deliveries">
          {(data.deliveries || []).length === 0 ? <EmptyState title="Belum ada pengiriman" /> : (
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              {data.deliveries.map((d) => (
                <div key={d.id} className="flex items-center justify-between p-3 border-b border-border last:border-0 text-sm">
                  <div><p className="font-medium">{d.order_number} · {d.method} {d.courier}</p><p className="text-xs text-muted-foreground">{d.tracking_number || "-"}</p></div>
                  <StatusBadge status={d.status} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history">
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            {[...(data.orders || []).map((o) => ({ t: o.created_at || o.order_date, label: `Order ${o.order_number} dibuat`, v: formatIDR(o.order_total) })),
              ...(data.payments || []).map((p) => ({ t: p.payment_date, label: `Bayar ${p.order_number} (${p.method})`, v: formatIDR(p.amount_idr) }))]
              .sort((a, b) => new Date(b.t) - new Date(a.t)).map((h, i) => (
                <div key={i} className="flex items-center justify-between p-3 border-b border-border last:border-0 text-sm">
                  <span>{h.label}</span><span className="text-xs text-muted-foreground">{formatDate(h.t)} · {h.v}</span>
                </div>
              ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
